import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Create a single particle group for a specific section
const SectionParticles = ({ sectionIndex, objectsDistance, particleTexture }) => {
  const particlesRef = useRef();
  const { camera } = useThree();
  const frameSkip = useRef(0);
  const frameSkipRate = 2; // Only update particles every 2 frames
  
  // Create particles for this section
  const { particlesGeometry, initialVelocities } = useMemo(() => {
    const particlesCount = 10; // Fewer particles per section for better performance
    const positions = new Float32Array(particlesCount * 3);
    const sizes = new Float32Array(particlesCount);
    const colors = new Float32Array(particlesCount * 3);
    const initialVelocities = new Array(particlesCount);
    
    // Brighter ocean color variations that will work well with bloom
    const baseColor = new THREE.Color('#0088ee'); // Brighter ocean blue
    const lightBlue = new THREE.Color('#99eeff'); // Brighter light blue
    const deepBlue = new THREE.Color('#0055aa'); // Brighter deep blue
    const tealBlue = new THREE.Color('#00aaaa'); // Brighter teal
    const whiteBlue = new THREE.Color('#bbddff'); // Almost white blue for extra bloom
    
    const colorOptions = [baseColor, lightBlue, deepBlue, tealBlue, whiteBlue];
    
    // Calculate the y-position range for this section
    const sectionYTop = objectsDistance * 0.8 - sectionIndex * objectsDistance;
    const sectionYBottom = sectionYTop - objectsDistance * 1.5; // Overlap slightly with adjacent sections
    
    for(let i = 0; i < particlesCount; i++) {
      // Position particles in a more water-like distribution
      positions[i * 3 + 0] = (Math.random() - 0.5) * 6; // x position
      
      // Position particles within this section's y-range
      positions[i * 3 + 1] = sectionYTop - Math.random() * (sectionYTop - sectionYBottom);
      
      positions[i * 3 + 2] = (Math.random() - 0.5) * 12.15; // z position
      
      // Make particles larger to ensure visibility
      sizes[i] = Math.random() * 0.1 + 0.1;
      
      // Assign random colors from our ocean palette
      const colorIndex = Math.floor(Math.random() * colorOptions.length);
      const color = colorOptions[colorIndex];
      colors[i * 3 + 0] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
      
      // Create initial velocities for gentle floating motion
      initialVelocities[i] = {
        x: (Math.random() - 0.5) * 0.005, // Very slow x movement
        y: (Math.random() - 0.5) * 0.004, // Even slower y movement
        z: (Math.random() - 0.5) * 0.002, // Extremely slow z movement
        waveAmplitude: Math.random() * 0.05 + 0.015,
        waveFrequency: Math.random() * 0.2 + 0.1,
        waveOffset: Math.random() * Math.PI * 2
      };
    }
    
    const particlesGeometry = new THREE.BufferGeometry();
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particlesGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    return { particlesGeometry, initialVelocities };
  }, [objectsDistance, sectionIndex]);
  
  // Temporary vector for calculations
  const _tempVector = new THREE.Vector3();
  
  // Animation with performance optimizations
  useFrame(({ clock }) => {
    if (particlesRef.current) {
      // Skip frames to reduce CPU load
      frameSkip.current = (frameSkip.current + 1) % frameSkipRate;
      if (frameSkip.current !== 0) {
        // Only rotate on skipped frames
        particlesRef.current.rotation.y += 0.0001;
        return;
      }
      
      const elapsedTime = clock.getElapsedTime();
      const positions = particlesRef.current.geometry.attributes.position.array;
      
      // Get camera position for distance-based culling
      const cameraPosition = camera.position;
      
      // Update each particle position for gentle floating motion
      for(let i = 0; i < positions.length / 3; i++) {
        const i3 = i * 3;
        const velocity = initialVelocities[i];
        
        // Create a temporary vector for distance calculation
        const particlePos = _tempVector.set(
          positions[i3],
          positions[i3 + 1],
          positions[i3 + 2]
        );
        
        // Distance-based culling - only animate particles within a certain distance from camera
        const distanceToCamera = particlePos.distanceTo(cameraPosition);
        if (distanceToCamera > 30) {
          // Skip updates for distant particles
          continue;
        }
        
        // Simplified wave calculation
        const timeOffset = elapsedTime * velocity.waveFrequency + velocity.waveOffset;
        const sinValue = Math.sin(timeOffset);
        const cosValue = Math.cos(timeOffset);
        
        const waveX = sinValue * velocity.waveAmplitude;
        const waveZ = cosValue * velocity.waveAmplitude;
        
        // Update positions with gentle drifting and wave motion
        positions[i3] += velocity.x + waveX * 0.02;
        positions[i3 + 1] += velocity.y * sinValue * 0.1;
        positions[i3 + 2] += velocity.z + waveZ * 0.05;
        
        // Boundary checks
        const boundary = 5;
        if (Math.abs(positions[i3]) > boundary) {
          positions[i3] = Math.sign(positions[i3]) * boundary;
          velocity.x *= -0.99;
        }
        
        const zBoundary = 5; // Increased from 0.1 to 5 to allow particles to spread on Z axis
        if (Math.abs(positions[i3 + 2]) > zBoundary) {
          positions[i3 + 2] = Math.sign(positions[i3 + 2]) * zBoundary;
          velocity.z *= -0.99;
        }
        
        // Calculate the y-position range for this section
        const sectionYTop = objectsDistance * 0.8 - sectionIndex * objectsDistance;
        const sectionYBottom = sectionYTop - objectsDistance * 1.5;
        
        // Keep particles within this section's y-range
        if (positions[i3 + 1] < sectionYBottom || positions[i3 + 1] > sectionYTop) {
          positions[i3 + 1] = sectionYTop - Math.random() * (sectionYTop - sectionYBottom);
        }
      }
      
      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });
  
  return (
    <points 
      ref={particlesRef} 
      geometry={particlesGeometry}
      frustumCulled={false}
    >
      <pointsMaterial 
        size={0.3}
        sizeAttenuation={true}
        transparent={true}
        opacity={0.5}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        vertexColors={true}
        map={particleTexture}
      />
    </points>
  );
};

const Particles = ({ objectsDistance = 4, scrollY = 0 }) => {
  const { camera } = useThree();
  
  // Count the number of sections in Sections.jsx
  const sectionCount = 14;
  
  // Track which sections are currently visible
  const [visibleSections, setVisibleSections] = useState([0, 1, 2]); // Start with first 3 sections visible
  
  // Create particle texture once and reuse for all sections
  const particleTexture = useMemo(() => {
    // Create a canvas for the sparkle texture with extra size for blur
    const canvas = document.createElement('canvas');
    canvas.width = 128; // Larger canvas for better blur quality
    canvas.height = 128;
    const context = canvas.getContext('2d');
    
    // Clear canvas with transparent background
    context.clearRect(0, 0, 128, 128);
    
    // Center point
    const centerX = 64;
    const centerY = 64;
    
    // Function to draw a gradient ray
    const drawRay = (fromX, fromY, toX, toY, width) => {
      // Create a gradient along the ray
      const gradient = context.createLinearGradient(fromX, fromY, toX, toY);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)'); // Near center: more opaque
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.4)'); // Middle: semi-transparent
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)'); // Edge: fully transparent
      
      // Draw the ray as a line with gradient
      context.beginPath();
      context.moveTo(fromX, fromY);
      context.lineTo(toX, toY);
      context.strokeStyle = gradient;
      context.lineWidth = width;
      context.lineCap = 'round'; // Rounded ends for softer look
      context.stroke();
    };
    
    // Draw main rays with gradients (4 directions)
    drawRay(centerX, centerY, centerX, centerY - 50, 4); // Top
    drawRay(centerX, centerY, centerX, centerY + 50, 4); // Bottom
    drawRay(centerX, centerY, centerX - 50, centerY, 4); // Left
    drawRay(centerX, centerY, centerX + 50, centerY, 4); // Right
    
    // Draw diagonal rays with gradients (4 directions, slightly shorter)
    const diagonalLength = 35;
    drawRay(centerX, centerY, centerX - diagonalLength, centerY - diagonalLength, 3); // Top-left
    drawRay(centerX, centerY, centerX + diagonalLength, centerY + diagonalLength, 3); // Bottom-right
    drawRay(centerX, centerY, centerX - diagonalLength, centerY + diagonalLength, 3); // Bottom-left
    drawRay(centerX, centerY, centerX + diagonalLength, centerY - diagonalLength, 3); // Top-right
    
    // Add larger center glow
    const centerGradient = context.createRadialGradient(
      centerX, centerY, 0,    // Inner circle center and radius
      centerX, centerY, 20    // Outer circle center and radius (increased)
    );
    
    // Add color stops for the center glow
    centerGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');   // Center: white, mostly opaque
    centerGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.5)'); // Middle: semi-transparent
    centerGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');     // Edge: transparent
    
    // Fill the center with the gradient
    context.fillStyle = centerGradient;
    context.beginPath();
    context.arc(centerX, centerY, 20, 0, Math.PI * 2);
    context.fill();
    
    // Apply blur effect
    try {
      // Try to use filter if supported
      context.filter = 'blur(11px)';
      context.drawImage(canvas, 0, 0);
      context.filter = 'none';
    } catch (e) {
      // If filter not supported, we'll rely on the gradient rays for softness
      console.log("Canvas filter not supported, using gradient softening only");
    }
    
    // Create a texture from the canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }, []);
  
  // Update visible sections based on camera position
  useFrame(() => {
    // Calculate which section the camera is currently looking at
    const currentSectionIndex = Math.round(Math.abs(camera.position.y) / objectsDistance);
    
    // Determine which sections should be visible (current section plus 1 before and 1 after)
    const newVisibleSections = [];
    for (let i = Math.max(0, currentSectionIndex - 1); i <= Math.min(sectionCount - 1, currentSectionIndex + 1); i++) {
      newVisibleSections.push(i);
    }
    
    // Only update state if the visible sections have changed
    if (JSON.stringify(newVisibleSections) !== JSON.stringify(visibleSections)) {
      setVisibleSections(newVisibleSections);
    }
  });
  
  return (
    <group>
      {/* Render only the particles for visible sections */}
      {visibleSections.map(sectionIndex => (
        <SectionParticles
          key={`particles-section-${sectionIndex}`}
          sectionIndex={sectionIndex}
          objectsDistance={objectsDistance}
          particleTexture={particleTexture}
        />
      ))}
    </group>
  );
};

export default Particles;
